/**
 * Pace disclosure of a large text blob so the UI does not jump from empty to wall-of-text
 * when the server (or provider) delivers one big chunk.
 */

const PASS_THROUGH_MAX = 40
const CHUNK_CHARS = 28
const FRAME_MS = 14

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Prefer breaking near a space so words don't pop mid-glyph. */
function nextBreak(text: string, start: number, prefer: number): number {
  const hard = Math.min(start + prefer, text.length)
  if (hard >= text.length) return text.length
  const window = text.slice(start, Math.min(start + prefer + 12, text.length))
  const space = window.lastIndexOf(' ')
  if (space > prefer * 0.4) return start + space + 1
  return hard
}

/**
 * Call `onSlice` with successive slices of `text`. Small strings pass through in one call.
 */
export async function revealTextProgressively(
  text: string,
  onSlice: (slice: string) => void,
  options?: { signal?: AbortSignal; chunkChars?: number; frameMs?: number }
): Promise<void> {
  if (!text) return
  const chunkChars = options?.chunkChars ?? CHUNK_CHARS
  const frameMs = options?.frameMs ?? FRAME_MS
  const signal = options?.signal

  if (text.length <= PASS_THROUGH_MAX) {
    onSlice(text)
    return
  }

  let i = 0
  while (i < text.length) {
    if (signal?.aborted) {
      onSlice(text.slice(i))
      return
    }
    const end = nextBreak(text, i, chunkChars)
    onSlice(text.slice(i, end))
    i = end
    if (i < text.length) {
      await sleep(frameMs)
    }
  }
}
